import React, { useCallback, CSSProperties } from 'react';
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    TouchSensor, MouseSensor
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from "react-bootstrap-icons";
import { QuestionTypeOrder } from "../../types/gameSession";
import { useAppDispatch, useAppSelector } from "../../app/store";
import { updateOrder } from "../../app/slices/gameSession";

interface Props {
    question: QuestionTypeOrder;
    showPoints: boolean;
}

interface OrderOptionProps {
    option: {
        index: number;
        option: string;
        ordering: number;
    };
    index: number;
    isAnswered: boolean;
    displayCorrectAnswers: boolean;
    isDragging?: boolean;
}

const OrderOption = React.memo(function OrderOption({
    option,
    index,
    isAnswered,
    displayCorrectAnswers,
    isDragging: externalIsDragging
}: OrderOptionProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({
        id: option.index,
        disabled: isAnswered
    });

    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: 'grab',
        zIndex: isDragging ? 1 : undefined,
    };

    const correctnessClass = displayCorrectAnswers
        ? (option.ordering === index + 1 ? 'selected correct' : 'selected incorrect')
        : '';

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`question-option-order ${isDragging ? 'dragging' : ''} ${correctnessClass}`}
            {...attributes}
            {...listeners}
        >
            <div className='question-option-col'>
                <div className='question-option-wrapper d-flex align-items-center justify-content-between'>
                    <span className='text-start'>{option.option}</span>
                    <span>
                        {!isAnswered ? (
                            <GripVertical width='2em' height='2em'/>
                        ) : (
                            <>{displayCorrectAnswers ? option.ordering : index + 1}.</>
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
});

function QuestionOrder({ question, showPoints }: Props) {
    const dispatch = useAppDispatch();
    const { data } = useAppSelector((state) => state.game);
    const displayCorrectAnswers = data?.display_correct_answers || false;

    const [activeId, setActiveId] = React.useState<number | null>(null);

    const sensors = useSensors(
        useSensor(MouseSensor, {
          activationConstraint: {
            distance: 10,
          },
        }),
        useSensor(TouchSensor, {
          activationConstraint: {
            delay: 10,
            tolerance: 5,
          },
        }),
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        setActiveId(event.active.id as number);
    }, []);

    const handleDragEnd = useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        setActiveId(null);
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = question.options.findIndex(item => item.index === active.id);
        const newIndex = question.options.findIndex(item => item.index === over.id);

        const reorderedOptions = arrayMove(question.options, oldIndex, newIndex);
        dispatch(updateOrder(reorderedOptions));
    }, [dispatch, question.options]);

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
        >
            <SortableContext
                items={question.options.map(option => option.index)}
                strategy={verticalListSortingStrategy}
            >
                <div className="droppable-container">
                    {question.options.map((option, index) => (
                        <OrderOption
                            key={option.index}
                            option={option}
                            index={index}
                            isAnswered={question.answered}
                            displayCorrectAnswers={displayCorrectAnswers}
                            isDragging={activeId === option.index}
                        />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}

const MemoizedQuestionOrder = React.memo(QuestionOrder);
export default MemoizedQuestionOrder;